<template>
  <div
    id="viewer"
    class="col-12 px-0 h-100"
  ></div>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { AttributeEvaluator, CityJSONLoader, CityJSONWorkerParser, CityObjectsMaterial, TextureManager } from 'cityjson-threejs-loader';
import { SRGBColorSpace } from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { GTAOPass, OutputPass, RenderPass } from 'three/examples/jsm/Addons.js';
import GUI from 'three/examples/jsm/libs/lil-gui.module.min.js';

export default {
	name: 'ThreeJsViewer',
	props: {
		citymodel: {
			type: Object,
			default: function () {

				return {};

			}
		},
		selectedObjid: {
			type: String,
			default: null
		},
		selectedGeomIdx: {
			type: Number,
			default: - 1,
		},
		selectedBoundaryIdx: {
			type: Number,
			default: - 1
		},
		highlightSelectedSurface: {
			type: Boolean,
			default: false
		},
		selectionColor: {
			type: Number,
			default: 0xffc107
		},
		objectColors: {
			type: Object,
			default: function () {

				return {
					"Building": 0x7497df,
					"BuildingPart": 0x7497df,
					"BuildingInstallation": 0x7497df,
					"Bridge": 0x999999,
					"BridgePart": 0x999999,
					"BridgeInstallation": 0x999999,
					"BridgeConstructionElement": 0x999999,
					"CityObjectGroup": 0xffffb3,
					"CityFurniture": 0xcc0000,
					"GenericCityObject": 0xcc0000,
					"LandUse": 0xffffb3,
					"PlantCover": 0x39ac39,
					"Railway": 0x000000,
					"Road": 0x999999,
					"SolitaryVegetationObject": 0x39ac39,
					"TINRelief": 0xffdb99,
					"TransportSquare": 0x999999,
					"Tunnel": 0x999999,
					"TunnelPart": 0x999999,
					"TunnelInstallation": 0x999999,
					"WaterBody": 0x4da6ff
				};

			}
		},
		surfaceColors: {
			type: Object,
			default: function () {

				return {
					"GroundSurface": 0x999999,
					"WallSurface": 0xffffff,
					"RoofSurface": 0xff0000,
					"TrafficArea": 0x6e6e6e,
					"AuxiliaryTrafficArea": 0x2c8200,
					"Window": 0x0059ff,
					"Door": 0x640000
				};

			}
		},
		backgroundColor: {
			type: Number,
			default: 0xd9eefc
		},
		showSemantics: {
			type: Boolean,
			default: true
		},
		doubleSide: {
			type: Boolean,
			default: true
		},
		ambientOcclusion: {
			type: Boolean,
			default: true
		},
		activeLod: {
			type: Number,
			default: - 1
		},
		cameraSpotlight: {
			type: Boolean,
			default: true
		},
		conditionalFormatting: {
			type: Boolean,
			default: false
		},
		conditionalAttribute: {
			type: String,
			default: null
		},
		attributeColors: {
			type: Object,
			default: () => {}
		},
		activeMaterialTheme: {
			type: String,
			default: "undefined"
		},
		textureManager: {
			type: TextureManager,
			default: undefined
		},
		activeTextureTheme: {
			type: String,
			default: "undefined"
		},
		hiddenObjects: {
			type: Set,
			default: () => new Set()
		},
	},
	data() {

		return {

			camera_init: true,
			lods: [],
			previousPos: {
				x: - 1,
				y: - 1
			},
			parser: null,
			autoRotationId: null,  // 存储动画帧ID
			rotationAngle: 0,      // 当前旋转角度
			rotationRadius: 0,     // 旋转半径
			rotationCenter: null,  // 旋转中心点
			rotationHeight: 0      // 相机高度（z轴）

		};

	},
	watch: {
		backgroundColor: function ( newVal ) {

			this.renderer.setClearColor( newVal );

			this.updateScene();

		},
		objectColors: {
			handler: function () {

				this.refreshColors();
				this.updateScene();

			},
			deep: true
		},
		surfaceColors: {
			handler: function () {

				this.refreshColors();
				this.updateScene();

			},
			deep: true
		},
		selectionColor: function () {

			this.refreshColors();
			this.updateScene();

		},
		citymodel: {
			handler: function ( newCitymodel ) {

				this.clearScene();

				this.loadCitymodel( newCitymodel );

				this.updateScene();

			},
			deep: true
		},
		selectedObjid: function () {

			this.updateScene();

		},
		selectedGeomIdx: function () {

			this.updateScene();

		},
		selectedBoundaryIdx: function () {

			this.updateScene();

		},
		doubleSide: function () {

			this.scene.traverse( c => {

				if ( c.material && c.material.isCityObjectsMaterial ) {

					c.material.side = this.doubleSide ? THREE.DoubleSide : THREE.FrontSide;

				}

			} );

			this.updateScene();

		},
		ambientOcclusion: function () {

			if ( this.ambientOcclusion ) {

				this.composer.addPass( this.gtaoPass );

			} else {

				this.composer.removePass( this.gtaoPass );

			}

			this.updateScene();

		},
		highlightSelectedSurface: function () {

			this.scene.traverse( c => {

				if ( c.material && c.material.isCityObjectsMaterial ) {

					c.material.selectSurface = this.highlightSelectedSurface;

				}

			} );

			this.updateScene();

		},
		showSemantics: function ( value ) {

			this.scene.traverse( c => {

				if ( c.material && c.material.isCityObjectsMaterial ) {

					c.material.showSemantics = value;

				}

			} );

			this.updateScene();

		},
		activeLod: function ( lodIdx ) {

			this.scene.traverse( c => {

				if ( c.material && c.material.isCityObjectsMaterial ) {

					c.material.showLod = lodIdx;

				}

			} );

			this.updateScene();

		},
		cameraSpotlight: function () {

			this.updateScene();

		},
		conditionalFormatting: function ( value ) {

			if ( this.conditionalAttribute == '' || this.conditionalAttribute === null ) {

				return;

			}

			this.scene.traverse( c => {

				if ( c.isCityObject ) {

					c.material.conditionalFormatting = value;

				}

			} );

			this.updateScene();

		},
		conditionalAttribute: function ( value ) {

			this.updateConditionalInfo();

			this.updateScene();

		},
		activeMaterialTheme: function ( theme ) {

			this.scene.traverse( c => {

				if ( c.supportsMaterials ) {

					c.material.materialTheme = theme;

				}

			} );

			this.updateScene();

		},
		activeTextureTheme: function ( theme ) {

			const scope = this;

			this.scene.traverse( c => {

				if ( c.supportsMaterials ) {

					c.setTextureTheme( theme, scope.textureManager );

				}

			} );

			this.updateScene();

		},
		attributeColors: {
			handler: function () {

				this.scene.traverse( c => {

					if ( c.isCityObject ) {

						c.material.attributeColors = this.attributeColors;

					}

				} );

				this.updateScene();

			},
			deep: true
		},
		hiddenObjects: {
			handler: function () {

				this.updateHiddenObjects();
				this.updateScene();

			},
			deep: true
		}
	},
	beforeCreate() {

		this.scene = null;
		this.camera = null;
		this.renderer = null;
		this.controls = null;
		this.raycaster = null;
		this.mouse = null;
		this.spotLight = null;
		this.gtaoPass = null;
		this.composer = null;

	},
	mounted() {

		this.initScene();

		this.loadCitymodel( this.citymodel );

		this.updateScene();

		this.renderer.domElement.addEventListener( 'pointerdown', this.pointerDown, false );
		this.renderer.domElement.addEventListener( 'pointermove', this.pointerMove, false );
		this.renderer.domElement.addEventListener( 'pointerup', this.pointerUp, false );

	},
	methods: {
		loadCitymodel( citymodel ) {

			this.$emit( 'rendering', true );

			if ( Object.keys( citymodel ).length > 0 ) {

				this.parser = new CityJSONWorkerParser();
				this.parser.chunkSize = 2000;

				const scope = this;
				this.parser.onChunkLoad = () => {

					scope.lods = scope.parser.lods;

					this.$emit( 'objectColorsChanged', scope.parser.objectColors );
					this.$emit( 'surfaceColorsChanged', scope.parser.surfaceColors );

					scope.refreshColors();

					scope.updateScene();

					scope.$emit( 'chunkLoaded' );

				};

				this.parser.onComplete = () => {

					scope.$emit( 'rendering', false );
					scope.$emit( 'loadCompleted' );

					scope.scene.traverse( c => {

						if ( c.isCityObject ) {

							c.material.side = this.doubleSide ? THREE.DoubleSide : THREE.FrontSide;

						}

					} );

				};

				const loader = new CityJSONLoader( this.parser );
				loader.load( citymodel );

				const bbox = loader.boundingBox.clone();
				bbox.applyMatrix4( loader.matrix );

				this.fitCameraToSelection( this.camera, this.controls, bbox );

				this.scene.add( loader.scene );

			}

		},
		fitCameraToSelection( camera, controls, box, fitOffset = 1.2 ) {

			// From https://discourse.threejs.org/t/camera-zoom-to-fit-object/936/24

			// const box.makeEmpty();
			// for ( const object of selection ) {

			//   box.expandByObject( object );

			// }
			const size = new THREE.Vector3();
			const center = new THREE.Vector3();

			box.getSize( size );
			box.getCenter( center );

			const maxSize = Math.max( size.x, size.y, size.z );
			const fitHeightDistance = maxSize / ( 2 * Math.atan( Math.PI * camera.fov / 360 ) );
			const fitWidthDistance = fitHeightDistance / camera.aspect;
			const distance = fitOffset * Math.max( fitHeightDistance, fitWidthDistance );

			const direction = controls.target.clone()
				.sub( camera.position )
				.normalize()
				.multiplyScalar( distance );

			controls.maxDistance = distance * 10;
			controls.target.copy( center );

			camera.near = distance / 100;
			camera.far = distance * 100;
			camera.updateProjectionMatrix();

			camera.position.copy( controls.target ).sub( direction );

			controls.update();

		},
		updateScene() {

			if ( this.cameraSpotlight ) {

				this.spotLight.position.copy( this.camera.position );

			}

			const idx = Object.keys( this.citymodel.CityObjects ).indexOf( this.selectedObjid );

			this.scene.traverse( c => {

				if ( c.material ) {

					const mats = Array.isArray( c.material ) ? c.material : [ c.material ];

					for ( const mat of mats ) {

						if ( mat.isCityObjectsMaterial ) {

							mat.selectSurface = this.highlightSelectedSurface;

							mat.highlightedObject = {

								objectIndex: idx,
								geometryIndex: this.selectedGeomIdx,
								boundaryIndex: this.selectedBoundaryIdx

							};

						}

					}

				}

			} );

			// this.renderer.render( this.scene, this.camera );
			this.composer.render();

		},
		refreshColors() {

			const scope = this;

			this.scene.traverse( mesh => {

				if ( mesh.material && mesh.material.isCityObjectsMaterial ) {

					mesh.material.objectColors = this.objectColors;
					mesh.material.surfaceColors = this.surfaceColors;

					mesh.material.highlightColor = scope.selectionColor;

				}

			} );

		},
		updateHiddenObjects() {
			// 将隐藏对象的ID转换为索引Set
			const objectIds = Object.keys( this.citymodel.CityObjects );
			const hiddenIndicesSet = new Set();
			
			this.hiddenObjects.forEach( objId => {
				const idx = objectIds.indexOf( objId );
				if ( idx > -1 ) {
					hiddenIndicesSet.add( idx );
				}
			} );

			// 遍历场景中的所有mesh，更新hidden属性
			this.scene.traverse( c => {
				if ( c.isCityObject && c.geometry ) {
					const objectIdAttr = c.geometry.getAttribute( 'objectid' );
					if ( objectIdAttr ) {
						// 创建或获取hidden属性数组
						let hiddenAttr = c.geometry.getAttribute( 'hidden' );
						if ( !hiddenAttr ) {
							const hiddenArray = new Float32Array( objectIdAttr.count );
							hiddenAttr = new THREE.BufferAttribute( hiddenArray, 1 );
							c.geometry.setAttribute( 'hidden', hiddenAttr );
						}
						
						// 更新hidden属性值
						const objectIds = objectIdAttr.array;
						const hiddenArray = hiddenAttr.array;
						for ( let i = 0; i < objectIds.length; i++ ) {
							hiddenArray[i] = hiddenIndicesSet.has( objectIds[i] ) ? 1.0 : 0.0;
						}
						hiddenAttr.needsUpdate = true;
						
						// 确保材质支持hidden功能
						this.setupHiddenShader( c.material );
					}
				}
			} );
		},
		setupHiddenShader( material ) {
			if ( material._hiddenSetup ) return;
			material._hiddenSetup = true;
			
			const originalOnBeforeCompile = material.onBeforeCompile;
			material.onBeforeCompile = ( shader ) => {
				if ( originalOnBeforeCompile ) {
					originalOnBeforeCompile( shader );
				}
				
				// 在vertex shader中添加hidden属性声明
				shader.vertexShader = shader.vertexShader.replace(
					'void main() {',
					`
					attribute float hidden;
					varying float vHidden;
					void main() {
						vHidden = hidden;
					`
				);
				
				// 在fragment shader中添加discard逻辑
				shader.fragmentShader = shader.fragmentShader.replace(
					'void main() {',
					`
					varying float vHidden;
					void main() {
						if ( vHidden > 0.5 ) discard;
					`
				);
			};
			material.needsUpdate = true;
		},
		updateConditionalInfo() {

			if ( this.conditionalAttribute ) {

				const evaluator = new AttributeEvaluator( this.citymodel, this.conditionalAttribute );
				const colors = evaluator.createColors();

				this.$emit( 'attributeColorsChanged', colors );

				this.scene.traverse( c => {

					if ( c.isCityObject ) {

						c.addAttributeByProperty( evaluator );
						c.material.attributeColors = colors;

					}

				} );

			}

		},
		pointerDown( e ) {

			this.previousPos.x = e.clientX;
			this.previousPos.y = e.clientY;

		},
		pointerUp( e ) {

			if ( this.previousPos.x == e.clientX && this.previousPos.y == e.clientY ) {

				this.handleClick( e );

			}

		},
		pointerMove( e ) {

			if ( e.ctrlKey ) {

				this.handleClick( e );

			}

		},
		getActiveIntersection( results ) {

			// Filters through the results to find the first one for the active LoD

			if ( this.activeLod > - 1 ) {

				for ( let i = 0; i < results.length; i ++ ) {

					const lodIdx = results[ i ].object.resolveIntersectionInfo( results[ i ], this.citymodel ).lodIndex;

					if ( lodIdx == this.activeLod ) {

						return results[ i ];

					}

				}

			}

			return results[ 0 ];

		},
		handleClick( e ) {

			var rect = this.renderer.domElement.getBoundingClientRect();
			this.mouse.x = ( ( e.clientX - rect.left ) / this.renderer.domElement.clientWidth ) * 2 - 1;
			this.mouse.y = - ( ( e.clientY - rect.top ) / this.renderer.domElement.clientHeight ) * 2 + 1;

			//get cameraposition
			this.raycaster.setFromCamera( this.mouse, this.camera );

			//calculate intersects
			var intersects = this.raycaster.intersectObject( this.scene, true );

			//if clicked on nothing return
			if ( intersects.length == 0 ) {

				this.$emit( 'object_clicked', null );
				return;

			}

			// 过滤掉隐藏的对象，找到第一个可见的对象
			let visibleIntersection = null;
			for ( const intersection of intersects ) {
				// 先应用 LoD 过滤
				if ( this.activeLod > -1 ) {
					const lodAttr = intersection.object.geometry?.getAttribute( 'lodid' );
					if ( lodAttr ) {
						const lodId = lodAttr.array[ intersection.face.a ];
						if ( lodId !== this.activeLod ) continue;
					}
				}
				
				// 检查是否为隐藏对象
				if ( intersection.object.isCityObject && this.hiddenObjects.size > 0 ) {
					const info = intersection.object.resolveIntersectionInfo( intersection, this.citymodel );
					if ( info && this.hiddenObjects.has( info.objectId ) ) {
						continue; // 跳过隐藏的对象
					}
				}
				
				visibleIntersection = intersection;
				break;
			}

			if ( !visibleIntersection ) {
				this.$emit( 'object_clicked', null );
				return;
			}

			if ( visibleIntersection.object.isCityObject ) {

				const info = visibleIntersection.object.resolveIntersectionInfo( visibleIntersection, this.citymodel );

				this.$emit( 'object_clicked', [ info.objectId, info.geometryIndex, info.boundaryIndex ] );

			}

		},
		getParams() {

			const hash = window.location.hash;

			if ( hash ) {

				const params = new URLSearchParams( hash.substring( 1 ) );
				return Object.fromEntries( params );

			} else {

				return {};

			}

		},
		initScene() {

			const viewer = document.getElementById( "viewer" );
			const ratio = viewer.clientWidth / viewer.clientHeight;

			this.scene = new THREE.Scene();
			this.camera = new THREE.PerspectiveCamera( 60, ratio, 0.0001, 4000 );
			this.camera.position.set( 0, - 1, 1 );
			this.camera.up.set( 0, 0, 1 );

			this.renderer = new THREE.WebGLRenderer( {
				antialias: window.devicePixelRatio > 1 ? false : true,
				powerPreference: "high-performance"
			} );
			this.renderer.outputEncoding = SRGBColorSpace;
			viewer.appendChild( this.renderer.domElement );
			this.renderer.setSize( viewer.clientWidth, viewer.clientHeight );
			this.renderer.setClearColor( this.backgroundColor );
			this.renderer.setPixelRatio( window.devicePixelRatio );

			const composer = new EffectComposer( this.renderer );
			this.composer = composer;

			const renderPass = new RenderPass( this.scene, this.camera );
			composer.addPass( renderPass );

			this.gtaoPass = new GTAOPass( this.scene, this.camera, viewer.clientWidth, viewer.clientHeight );

			const aoParameters = {
				radius: 2.4,
				distanceExponent: 1.,
				thickness: 10.,
				scale: 1.3,
				samples: 16,
				distanceFallOff: 1.,
				screenSpaceRadius: false,
			};

			this.gtaoPass.updateGtaoMaterial( aoParameters );

			if ( this.ambientOcclusion ) {

				composer.addPass( this.gtaoPass );

			}

			const outputPass = new OutputPass();
			composer.addPass( outputPass );

			const updateGtaoMaterial = () => {

				gtaoPass.updateGtaoMaterial( aoParameters );
				this.updateScene();

			};

			if ( "debug" in this.getParams() ) {

				const gui = new GUI();

				gui.add( gtaoPass, 'blendIntensity' ).min( 0 ).max( 1 ).step( 0.01 );
				gui.add( aoParameters, 'radius' ).min( 0.01 ).max( 10 ).step( 0.1 ).onChange( updateGtaoMaterial );
				gui.add( aoParameters, 'distanceExponent' ).min( 1 ).max( 4 ).step( 0.01 ).onChange( updateGtaoMaterial );
				gui.add( aoParameters, 'thickness' ).min( 0.01 ).max( 10 ).step( 0.1 ).onChange( updateGtaoMaterial );
				gui.add( aoParameters, 'distanceFallOff' ).min( 0 ).max( 1 ).step( 0.01 ).onChange( updateGtaoMaterial );
				gui.add( aoParameters, 'scale' ).min( 0.01 ).max( 2.0 ).step( 0.1 ).onChange( updateGtaoMaterial );
				gui.add( aoParameters, 'samples' ).min( 2 ).max( 32 ).step( 1 ).onChange( updateGtaoMaterial );
				gui.add( aoParameters, 'screenSpaceRadius' ).onChange( updateGtaoMaterial );

			}

			let self = this;

			this.raycaster = new THREE.Raycaster();
			this.mouse = new THREE.Vector2();

			// 设置环境光为较低强度，避免过度均匀照明
			const ambientLight = new THREE.AmbientLight( 0xffffff, 0.3 * Math.PI );
			this.scene.add( ambientLight );

			// 主光源 - 从右上方照射，提供主要照明和阴影
			this.spotLight = new THREE.DirectionalLight( 0xffffff, 0.8 * Math.PI );
			this.spotLight.position.set( 5, 5, 3 );
			this.spotLight.castShadow = true;
			this.scene.add( this.spotLight );

			// 补光 - 从左侧照射，填充阴影区域
			const fillLight = new THREE.DirectionalLight( 0xffffff, 0.4 * Math.PI );
			fillLight.position.set( - 5, 3, 2 );
			this.scene.add( fillLight );

			// 轮廓光 - 从后方照射，突出边缘
			const rimLight = new THREE.DirectionalLight( 0xffffff, 0.3 * Math.PI );
			rimLight.position.set( 0, - 3, 5 );
			this.scene.add( rimLight );

			this.controls = new OrbitControls( this.camera, this.renderer.domElement );
			this.controls.addEventListener( 'change', function () {

				self.updateScene();

			} );
			this.controls.target.set( 0, 0, 0 );

			const scope = this;
			window.addEventListener( 'resize', _ => {

				// 判断是透视相机还是正交相机
				if ( scope.camera.isPerspectiveCamera ) {

					scope.camera.aspect = viewer.clientWidth / viewer.clientHeight;

				} else if ( scope.camera.isOrthographicCamera ) {

					// 正交相机需要更新视锥体参数
					const aspect = viewer.clientWidth / viewer.clientHeight;
					const frustumHeight = scope.camera.top - scope.camera.bottom;
					const frustumWidth = frustumHeight * aspect;
					
					scope.camera.left = frustumWidth / - 2;
					scope.camera.right = frustumWidth / 2;

				}
				
				scope.camera.updateProjectionMatrix();

				scope.renderer.setSize( viewer.clientWidth, viewer.clientHeight );

				scope.updateScene();

			}, false );

		},
		clearScene() {

			// ✅ 只移除不是光源的对象，保留已有光源，避免重复添加
			this.scene.children = this.scene.children.filter( c => c.isLight );

			// ✅ 如果场景里没有光源，重新添加（保证第一次切换版本也有光）
			const hasLights = this.scene.children.some( c => c.isLight );
			if ( ! hasLights ) {

				// 设置环境光为较低强度，避免过度均匀照明
				const ambientLight = new THREE.AmbientLight( 0xffffff, 0.3 * Math.PI );
				this.scene.add( ambientLight );

				// 主光源 - 从右上方照射，提供主要照明和阴影
				this.spotLight = new THREE.DirectionalLight( 0xffffff, 0.8 * Math.PI );
				this.spotLight.position.set( 5, 5, 3 );
				this.spotLight.castShadow = true;
				this.scene.add( this.spotLight );

				// 补光 - 从左侧照射，填充阴影区域
				const fillLight = new THREE.DirectionalLight( 0xffffff, 0.4 * Math.PI );
				fillLight.position.set( - 5, 3, 2 );
				this.scene.add( fillLight );

				// 轮廓光 - 从后方照射，突出边缘
				const rimLight = new THREE.DirectionalLight( 0xffffff, 0.3 * Math.PI );
				rimLight.position.set( 0, - 3, 5 );
				this.scene.add( rimLight );

			}

		},
		// clearScene() {

		// 	while ( this.scene.children.length > 0 ) {

		// 		this.scene.remove( this.scene.children[ 0 ] );

		// 	}

		// 	var am_light = new THREE.AmbientLight( 0xFFFFFF, 0.7 ); // soft white light
		// 	this.scene.add( am_light );

		// 	// Add directional light
		// 	var spot_light = new THREE.SpotLight( 0xDDDDDD );
		// 	spot_light.position.set( 84616, - 1, 447422 );
		// 	spot_light.target = this.scene;
		// 	spot_light.castShadow = true;
		// 	spot_light.intensity = 0.4;
		// 	spot_light.position.normalize();
		// 	this.scene.add( spot_light );

		// },
		getLods() {

			return this.lods;

		},
		setCameraView( direction ) {

			// 获取场景的包围盒以确定相机距离
			const bbox = new THREE.Box3();
			this.scene.traverse( ( obj ) => {

				if ( obj.isMesh ) {

					bbox.expandByObject( obj );

				}

			} );

			const center = new THREE.Vector3();
			bbox.getCenter( center );
			const size = bbox.getSize( new THREE.Vector3() );
			const maxDim = Math.max( size.x, size.y, size.z );
			const distance = maxDim * 2; // 相机距离

			// 获取viewer尺寸
			const viewer = document.getElementById( "viewer" );
			const aspect = viewer.clientWidth / viewer.clientHeight;

			// 创建正交相机，替换透视相机
			const frustumSize = maxDim * 1.2; // 视锥体大小
			
			this.camera = new THREE.OrthographicCamera(
				frustumSize * aspect / - 2, // left
				frustumSize * aspect / 2, // right
				frustumSize / 2, // top
				frustumSize / - 2, // bottom
				0.01, // near
				distance * 10 // far
			);

			// 根据方向设置相机位置
			const positions = {
				'front': new THREE.Vector3( center.x, center.y - distance, center.z ),
				'back': new THREE.Vector3( center.x, center.y + distance, center.z ),
				'left': new THREE.Vector3( center.x - distance, center.y, center.z ),
				'right': new THREE.Vector3( center.x + distance, center.y, center.z ),
				'top': new THREE.Vector3( center.x, center.y, center.z + distance ),
				'bottom': new THREE.Vector3( center.x, center.y, center.z - distance )
			};

			// 根据方向设置相机的上方向
			const upVectors = {
				'front': new THREE.Vector3( 0, 0, 1 ),
				'back': new THREE.Vector3( 0, 0, 1 ),
				'left': new THREE.Vector3( 0, 0, 1 ),
				'right': new THREE.Vector3( 0, 0, 1 ),
				'top': new THREE.Vector3( 0, 1, 0 ),
				'bottom': new THREE.Vector3( 0, - 1, 0 )
			};

			if ( positions[ direction ] ) {

				this.camera.position.copy( positions[ direction ] );
				this.camera.up.copy( upVectors[ direction ] );
				this.camera.lookAt( center );

				// 更新控制器的相机引用
				this.controls.object = this.camera;
				this.controls.target.copy( center );
				this.controls.update();

				// 更新渲染通道的相机引用
				this.composer.passes.forEach( pass => {

					if ( pass.camera ) {

						pass.camera = this.camera;

					}

				} );

				// 更新GTAO通道
				if ( this.gtaoPass ) {

					this.gtaoPass.camera = this.camera;

				}

				// 更新光源位置，让光源跟随相机，确保光照一致
				if ( this.cameraSpotlight ) {

					this.spotLight.position.copy( this.camera.position );

				}

				this.updateScene();

			}

		},
		resetToPerspectiveCamera() {

			// 如果已经是透视相机，则不需要切换
			if ( this.camera.isPerspectiveCamera ) {

				return;

			}

			// 保存当前位置和目标
			const currentPosition = this.camera.position.clone();
			const currentTarget = this.controls.target.clone();

			// 获取viewer尺寸
			const viewer = document.getElementById( "viewer" );
			const aspect = viewer.clientWidth / viewer.clientHeight;

			// 创建新的透视相机
			this.camera = new THREE.PerspectiveCamera( 60, aspect, 0.0001, 4000 );
			this.camera.position.copy( currentPosition );
			this.camera.up.set( 0, 0, 1 );
			this.camera.lookAt( currentTarget );

			// 更新控制器的相机引用
			this.controls.object = this.camera;
			this.controls.target.copy( currentTarget );
			this.controls.update();

			// 更新渲染通道的相机引用
			this.composer.passes.forEach( pass => {

				if ( pass.camera ) {

					pass.camera = this.camera;

				}

			} );

			// 更新GTAO通道
			if ( this.gtaoPass ) {

				this.gtaoPass.camera = this.camera;

			}

			this.updateScene();

		},
		// 开始自动旋转
		startAutoRotation() {

			// 停止之前的旋转（如果存在）
			this.stopAutoRotation();

			// 记录当前相机位置信息
			this.rotationCenter = this.controls.target.clone();
			this.rotationHeight = this.camera.position.z;

			// 计算旋转半径（相机到中心点在XY平面的距离）
			const dx = this.camera.position.x - this.rotationCenter.x;
			const dy = this.camera.position.y - this.rotationCenter.y;
			this.rotationRadius = Math.sqrt( dx * dx + dy * dy );

			// 计算初始角度
			this.rotationAngle = Math.atan2( dy, dx );

			// 开始旋转动画
			const animate = () => {

				this.autoRotationId = requestAnimationFrame( animate );

				// 每帧增加角度（控制旋转速度）
				this.rotationAngle += 0.005; // 调整这个值可以改变旋转速度

				// 计算新的相机位置（在XY平面上绕中心旋转）
				const newX = this.rotationCenter.x + this.rotationRadius * Math.cos( this.rotationAngle );
				const newY = this.rotationCenter.y + this.rotationRadius * Math.sin( this.rotationAngle );

				// 更新相机位置，保持Z轴高度不变
				this.camera.position.set( newX, newY, this.rotationHeight );

				// 相机始终看向中心点
				this.camera.lookAt( this.rotationCenter );

				// 更新控制器
				this.controls.update();

				// 更新光源位置（如果启用了相机光源）
				if ( this.cameraSpotlight ) {

					this.spotLight.position.copy( this.camera.position );

				}

			};

			animate();

		},
		// 停止自动旋转
		stopAutoRotation() {

			if ( this.autoRotationId !== null ) {

				cancelAnimationFrame( this.autoRotationId );
				this.autoRotationId = null;

			}

		}
	}
};
</script>
